import { useState } from 'react';
import SectionHeading from '@/pages/home/components/SectionHeading';
import Reveal from '@/components/base/Reveal';
import { timelineItems } from '@/mocks/home';

const filters = ['全部', '起点', '发酵', '回应', '反转', '进展'] as const;

const kindStyle: Record<string, { chip: string; dot: string }> = {
  起点: { chip: 'bg-secondary-100 text-secondary-900', dot: 'bg-secondary-500' },
  发酵: { chip: 'bg-accent-100 text-accent-900', dot: 'bg-accent-500' },
  回应: { chip: 'bg-primary-100 text-primary-700', dot: 'bg-primary-400' },
  反转: { chip: 'bg-primary-500 text-background-50', dot: 'bg-primary-500' },
  进展: { chip: 'bg-foreground-100 text-foreground-800', dot: 'bg-foreground-500' },
};

export default function TimelineSection() {
  const [filter, setFilter] = useState<string>('全部');

  const list =
    filter === '全部' ? timelineItems : timelineItems.filter((item) => item.kind === filter);

  return (
    <section id="timeline" className="scroll-mt-24 bg-background-100 py-16 md:py-24">
      <div className="mx-auto max-w-content px-4 md:px-6">
        <SectionHeading
          index="02"
          kicker="FULL TIMELINE"
          title="事件全貌：按时间把每一环都对上"
          desc="从官宣到关闭评论区，逐条列出已经公开的时间节点。点下方标签可以只看某一类节点，比如把所有「反转」单独拎出来对一遍。"
        />

        <div className="mt-8 flex flex-wrap gap-2">
          {filters.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              aria-pressed={filter === f}
              className={`whitespace-nowrap rounded-full px-4 py-2 font-label text-sm transition-colors cursor-pointer ${
                filter === f
                  ? 'bg-foreground-950 text-background-50'
                  : 'border border-background-300 bg-background-50 text-foreground-600 hover:border-foreground-300 hover:text-foreground-900'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <ol className="relative mt-10 ml-2 border-l border-background-300 pl-6 md:ml-4 md:pl-10">
          {list.map((item, i) => {
            const style = kindStyle[item.kind] ?? kindStyle['进展'];
            return (
              <Reveal as="li" key={`${item.date}-${item.title}`} delay={Math.min(i * 60, 300)}>
                <div className="relative pb-8 md:pb-10">
                  <span
                    className={`absolute -left-[31px] top-2 h-3 w-3 rounded-full ring-4 ring-background-100 md:-left-[47px] ${style.dot}`}
                  />
                  <div className="rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300 md:p-6">
                    <div className="flex flex-wrap items-center gap-3">
                      <time className="font-heading text-sm font-bold tracking-wide text-primary-600 md:text-base">
                        {item.date}
                      </time>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-label text-[11px] font-medium ${style.chip}`}
                      >
                        {item.kind}
                      </span>
                    </div>
                    <h3 className="mt-2 font-heading text-base font-bold text-foreground-950 md:text-lg">
                      {item.title}
                    </h3>
                    <p className="mt-2 font-body text-sm leading-relaxed text-foreground-600">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </ol>

        {list.length === 0 && (
          <p className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-50 py-10 text-center font-body text-sm text-foreground-500">
            这一类节点暂时没有记录。
          </p>
        )}
      </div>
    </section>
  );
}